import { usePermission } from "#app/lib/hooks/use-permission";

/**
 * Automation capabilities, split by what the UI actually gates. Everyone can
 * VIEW automations and their live/off state (visibility is deliberate — reps
 * should know an automation is acting on their entities); the write-side
 * capabilities all resolve from the single `automations.manage` permission
 * today.
 *
 * The split exists so a finer-grained model later (e.g. an operator role that
 * can turn automations on/off but not edit them) lands as a mapping change
 * HERE — not a hunt through every surface. UI code must gate on the specific
 * capability it exercises, never on `automations.manage` directly.
 */
export function useAutomationPermissions() {
  const canManage = usePermission("automations.manage");
  return {
    /** Create new automations (create buttons, template gallery, duplicate). */
    canCreate: canManage,
    /** Open the builder to edit graphs, rename, delete, switch versions. */
    canEdit: canManage,
    /** Flip enabled on/off and pick which version goes live. */
    canToggle: canManage,
    /** Open a per-issue automation-run log (spans every automation in the
     *  workspace, so it's an automations-admin surface). */
    canViewRuns: canManage,
    /** See the workspace-level Automations entry that opens the manager. */
    canOpenManager: canManage,
  } as const;
}
